import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Button, Radio, Divider, Typography, Alert } from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext, ThemeContext } from 'src/store/ContextStore';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import GailForm from './GailForm';
import IndividualDataForm from './IndividualDataForm';

/**
 * Risk Factor clinical record — one modal that toggles between the CanRisk
 * "Individual Data" form and the Gail score form (radio switch).
 * - Gail: GailForm (self-saving via its Calculate button → gail record).
 * - CanRisk: IndividualDataForm, prefilled for the proband, saved to the
 *   member's canrisk record. Created records surface as "Risk Score" tiles.
 */
export default function RiskFactorForm({
  data,
  handleClose,
  isProband,
  familyMemberId,
  isDisabled,
  setUnsavedChanges,
  model
}) {
  const { content } = useContext(AppContext);
  const { setSnack } = useContext(SnackContext);
  const { theme } = useContext(ThemeContext);
  const user = content?.pedigreeSelectedUsers?.selectedUser;
  const userId = user?.user_id || user?._id;

  // Settings-style card that holds the selected form below the divider.
  const isLight = theme?.appearance?.value === 'light';
  const formCardSx = {
    bgcolor: isLight ? '#fff' : 'rgba(255,255,255,0.05)',
    border: '0.5px solid',
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2.5,
    mb: 1
  };

  const gender = data?.gender;
  const gailAllowed = gender === 'female';

  // Canonical member key — same id everywhere (never the literal 'proband').
  const memberId = data?.proBandId ?? familyMemberId;

  // When opened from an existing card, preselect that record's model.
  const [riskModel, setRiskModel] = useState(
    model?.recordType || (gailAllowed ? 'gail' : 'canrisk')
  );
  const [individualData, setIndividualData] = useState({});
  const [savingCanrisk, setSavingCanrisk] = useState(false);
  const gailRef = useRef(null);

  // Score from the loaded record (if calculated), shown in an alert at the top.
  const canriskSummary = (result) => {
    const pr = result?.pedigree_result?.[0];
    const tenYr = pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const lifetime =
      pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    return tenYr != null && lifetime != null
      ? `10-year: ${tenYr}% | Lifetime: ${lifetime}%`
      : null;
  };

  let scoreAlert = null;
  if (
    riskModel === 'gail' &&
    model?.recordType === 'gail' &&
    model?.gailRiskScore != null
  ) {
    scoreAlert = `GAIL score: ${model.gailRiskScore}`;
  } else if (
    riskModel === 'canrisk' &&
    model?.recordType === 'canrisk' &&
    model?.canriskResult
  ) {
    scoreAlert =
      canriskSummary(model.canriskResult) || 'CanRisk scores calculated';
  }

  // Load the member's saved individual data (+ proband prefill) when the
  // CanRisk branch is active.
  useEffect(() => {
    if (riskModel !== 'canrisk' || !userId) return;
    axios
      .get(
        `${APICONSTANTS.fillCanRiskModal.replace(
          ':user_id',
          userId
        )}?targetId=${memberId}`
      )
      .then(({ data: resp }) => {
        const page = (resp.pages || []).find(
          (p) => String(p.familyMemberId) === String(memberId)
        );
        setIndividualData(page?.individualData || {});
      })
      .catch(() => {});
  }, [riskModel, userId, memberId]);

  const handleSaveCanrisk = () => {
    if (!userId) {
      return setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
    }
    setSavingCanrisk(true);
    axios
      .put(APICONSTANTS.saveCanRiskIndividualData.replace(':user_id', userId), {
        // Editing a CanRisk tile updates it; otherwise a new record is created.
        recordId: model?.recordType === 'canrisk' ? model?._id : undefined,
        familyMemberId: memberId,
        familyId: user?.familyId,
        individualData
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Individual data saved',
          severity: 'success'
        });
        handleClose({ exit: true });
      })
      .catch(() =>
        setSnack({
          status: true,
          msg: 'Error saving individual data',
          severity: 'error'
        })
      )
      .finally(() => setSavingCanrisk(false));
  };

  return (
    <Box sx={{ p: 1 }}>
      {scoreAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {scoreAlert}
        </Alert>
      )}
      <FormLabel>Risk model</FormLabel>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75 }}>
        {[
          {
            value: 'canrisk',
            label: 'CanRisk',
            description: 'Individual data inputs',
            disabled: isDisabled
          },
          {
            value: 'gail',
            label: 'Gail score',
            description: 'Gail (BCRAT) inputs',
            disabled: isDisabled || !gailAllowed
          }
        ].map((opt) => {
          const selected = riskModel === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => {
                if (opt.disabled) return;
                setRiskModel(opt.value);
                setUnsavedChanges?.({ status: false, edit: true });
              }}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                border: '1px solid',
                borderColor: selected ? 'secondary.main' : 'divider',
                bgcolor: selected ? 'action.hover' : 'transparent',
                borderRadius: '12px',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                opacity: opt.disabled ? 0.5 : 1
              }}
            >
              <Radio size="small" checked={selected} disabled={opt.disabled} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}
                >
                  {opt.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {opt.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {!gailAllowed && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 1
          }}
        >
          Gail score is only available for female individuals.
        </Typography>
      )}
      {/* Bar separating the mode selector from the record fields below. */}
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Box sx={formCardSx}>
        {riskModel === 'gail' ? (
          // Capture Gail inputs only (no calculation). The Save action lives in
          // the modal footer; the score is produced later from the sidebar
          // "Calculate Gail Score" action.
          <GailForm
            ref={gailRef}
            data={data}
            model={model?.recordType === 'gail' ? model : null}
            hideActionButton
            disabled={isDisabled}
            onSaved={() => handleClose({ exit: true })}
          />
        ) : (
          <IndividualDataForm
            value={individualData}
            onChange={(next) => {
              setIndividualData(next);
              setUnsavedChanges?.({ status: false, edit: true });
            }}
            disabled={isDisabled}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button variant="outlined" onClick={() => handleClose({ exit: true })}>
          Close
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() =>
            riskModel === 'gail'
              ? gailRef.current?.submit()
              : handleSaveCanrisk()
          }
          disabled={isDisabled || savingCanrisk}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}
